import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Stack,
} from '@mui/material';
import {
  Error as ErrorIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  QueryStats as QueryStatsIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Bolt as BoltIcon,
  TaskAlt as TaskAltIcon,
  HourglassBottom as HourglassIcon,
  Link as LinkIcon,
  CalendarMonth as CalendarMonthIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { anomaliesAPI, checkoutAdminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import QuickAnomalyResolver from './QuickAnomalyResolver';

type PeriodType = 'daily' | 'weekly' | 'monthly';
type SeverityFilter = 'all' | 'severe' | 'moderate' | 'minor';

interface AnomalyEmployee {
  employee_id: number;
  user_id?: number | null;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  dipartimento: string;
  ore_previste: number;
  ore_effettive: number;
  differenza: number;
  percentuale_completamento: number;
  gravita: 'severe' | 'moderate' | 'minor';
  periodo: string;
  anno?: number | null;
  mese?: number | null;
  giorno?: number | null;
}

interface AnomalySummary {
  total: number;
  severe: number;
  moderate: number;
  minor: number;
  employees: AnomalyEmployee[];
}

interface PendingCheckout {
  presence_id: number;
  user_id: number;
  employee_id: number;
  nome: string;
  cognome: string;
  matricola: string | null;
  dipartimento: string | null;
  check_in_time: string;
  notes?: string;
}

const severityIcons: Record<SeverityFilter, JSX.Element | null> = {
  all: <QueryStatsIcon fontSize="small" />,
  severe: <ErrorIcon fontSize="small" />,
  moderate: <WarningIcon fontSize="small" />,
  minor: <InfoIcon fontSize="small" />,
};

const severityColors: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  severe: 'error',
  moderate: 'warning',
  minor: 'info',
};

const severityLabels: Record<SeverityFilter, string> = {
  all: 'Tutte',
  severe: 'Critiche',
  moderate: 'Medie',
  minor: 'Lievi',
};

const periodLabels: Record<PeriodType, string> = {
  daily: 'Giornaliero',
  weekly: 'Settimanale',
  monthly: 'Mensile',
};

const formatHours = (value?: number) =>
  value !== undefined && value !== null ? `${value.toFixed(2)} h` : '-';

const formatDifference = (value: number) => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)} h`;
};

const calcHoursSince = (dateString: string) => {
  const checkIn = new Date(dateString);
  const now = new Date();
  const diff = (now.getTime() - checkIn.getTime()) / (1000 * 60 * 60);
  return diff > 0 ? diff : 0;
};

const AnomaliesManager: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<PeriodType>('weekly');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [anomalies, setAnomalies] = useState<AnomalySummary | null>(null);
  const [pendingCheckouts, setPendingCheckouts] = useState<PendingCheckout[]>([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Stato per dialog risoluzione rapida anomalie
  const [resolverOpen, setResolverOpen] = useState(false);
  const [resolverEmployee, setResolverEmployee] = useState<{
    name: string;
    userId: number;
    year: number;
    month: number;
  } | null>(null);

  // Selettori per periodo
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [selectedWeek, setSelectedWeek] = useState<string>(() => {
    // Calcola il lunedì della settimana scorsa
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    const dayOfWeek = lastWeek.getDay();
    const diff = lastWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Aggiusta per lunedì
    const monday = new Date(lastWeek.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    // Default: mese corrente
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  
  // Stato per alert anomalie mese precedente
  const [previousMonthAnomalies, setPreviousMonthAnomalies] = useState<{
    count: number;
    month: string;
    monthLabel: string;
  } | null>(null);

  const loadAnomalies = async (p: PeriodType) => {
    setLoading(true);
    setError(null);
    try {
      let response: AnomalySummary;
      switch (p) {
        case 'daily':
          response = await anomaliesAPI.getDailyAnomalies(selectedDate);
          break;
        case 'weekly':
          response = await anomaliesAPI.getWeeklyAnomalies(selectedWeek);
          break;
        case 'monthly':
          response = await anomaliesAPI.getMonthlyAnomalies(selectedMonth);
          break;
        default:
          response = await anomaliesAPI.getWeeklyAnomalies(selectedWeek);
      }
      // Normalizza la risposta per assicurarsi che employees sia sempre un array
      setAnomalies({
        ...response,
        employees: Array.isArray(response.employees) ? response.employees : [],
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossibile caricare le anomalie');
      setAnomalies(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCheckouts = async () => {
    setCheckoutLoading(true);
    try {
      const response = await checkoutAdminAPI.getPendingCheckouts();
      setPendingCheckouts(response.pending_checkouts || []);
    } catch (err) {
      // Errore caricamento checkout pendenti
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Carica anomalie del mese precedente per l'alert
  const checkPreviousMonthAnomalies = async () => {
    try {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      const monthStr = `${lastMonth.getFullYear()}-${(lastMonth.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthLabel = lastMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      
      const response = await anomaliesAPI.getMonthlyAnomalies(monthStr);
      if (response && response.total > 0) {
        setPreviousMonthAnomalies({
          count: response.total,
          month: monthStr,
          monthLabel: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
        });
      } else {
        setPreviousMonthAnomalies(null);
      }
    } catch (err) {
      // Ignora errori nel check del mese precedente
      setPreviousMonthAnomalies(null);
    }
  };

  useEffect(() => {
    loadAnomalies(period);
    loadPendingCheckouts();
    // Controlla anomalie mese precedente solo per periodo mensile
    if (period === 'monthly') {
      checkPreviousMonthAnomalies();
    } else {
      setPreviousMonthAnomalies(null);
    }
  }, [period, selectedDate, selectedWeek, selectedMonth]);

  useEffect(() => {
    loadPendingCheckouts();
  }, []);

  const filteredEmployees = useMemo(() => {
    if (!anomalies || !anomalies.employees || !Array.isArray(anomalies.employees)) return [];
    return anomalies.employees.filter((employee) => {
      const matchesSeverity =
        severity === 'all' ? true : employee.gravita === severity;

      const term = searchTerm.trim().toLowerCase();
      const matchesTerm =
        term.length === 0 ||
        `${employee.nome} ${employee.cognome}`.toLowerCase().includes(term) ||
        employee.dipartimento?.toLowerCase().includes(term) ||
        employee.codice_fiscale?.toLowerCase().includes(term);

      return matchesSeverity && matchesTerm;
    });
  }, [anomalies, severity, searchTerm]);

  const worstUnderTarget = useMemo(() => {
    if (!anomalies || !anomalies.employees || !Array.isArray(anomalies.employees)) return null;
    return [...anomalies.employees]
      .filter((emp) => emp.differenza < 0)
      .sort((a, b) => a.differenza - b.differenza)[0];
  }, [anomalies]);

  const mostOverTarget = useMemo(() => {
    if (!anomalies || !anomalies.employees || !Array.isArray(anomalies.employees)) return null;
    return [...anomalies.employees]
      .filter((emp) => emp.differenza > 0)
      .sort((a, b) => b.differenza - a.differenza)[0];
  }, [anomalies]);

  const handleForceCheckout = async (presenceId: number) => {
    if (!confirm('Vuoi forzare il checkout per questa presenza?')) return;
    try {
      setCheckoutLoading(true);
      await checkoutAdminAPI.forceCheckout(presenceId);
      setSuccessMessage('Checkout forzato con successo');
      await loadPendingCheckouts();
      await loadAnomalies(period);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore durante il checkout forzato');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleAutoCheckout = async () => {
    if (!confirm('Eseguire il checkout automatico per tutte le presenze oltre 12 ore?')) {
      return;
    }
    try {
      setCheckoutLoading(true);
      const result = await checkoutAdminAPI.autoCheckoutExpired(12);
      setSuccessMessage(`Checkout automatico completato per ${result.count} presenze`);
      await loadPendingCheckouts();
      await loadAnomalies(period);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore durante il checkout automatico');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="warning">
          Questa sezione appartiene all'area amministrativa delle anomalie.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1 }}>
            Gestione Anomalie Presenze
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitora scostamenti da orario previsto, straordinari e checkout dimenticati in un unico
            pannello operativo.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadAnomalies(period);
            loadPendingCheckouts();
          }}
          disabled={loading || checkoutLoading}
        >
          Aggiorna dati
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Alert anomalie mese precedente */}
      {period === 'monthly' && previousMonthAnomalies && selectedMonth !== previousMonthAnomalies.month && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setSelectedMonth(previousMonthAnomalies.month);
                setPreviousMonthAnomalies(null); // Nascondi alert dopo il click
              }}
            >
              Visualizza
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>{previousMonthAnomalies.count}</strong> anomalia/e rilevata/e per <strong>{previousMonthAnomalies.monthLabel}</strong>
          </Typography>
        </Alert>
      )}

      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs
          value={period}
          onChange={(_, value) => setPeriod(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab value="daily" label="Giorno" />
          <Tab value="weekly" label="Settimana" />
          <Tab value="monthly" label="Mese" />
        </Tabs>

        <Box px={3} py={2} display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
          {/* Selettore periodo specifico */}
          {period === 'daily' && (
            <TextField
              label="Data"
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            />
          )}
          {period === 'weekly' && (
            <TextField
              label="Lunedì della settimana"
              type="date"
              size="small"
              value={selectedWeek}
              onChange={(e) => {
                // Assicurati che sia un lunedì
                const date = new Date(e.target.value);
                const dayOfWeek = date.getDay();
                const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                const monday = new Date(date.setDate(diff));
                setSelectedWeek(monday.toISOString().split('T')[0]);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            />
          )}
          {period === 'monthly' && (
            <TextField
              label="Mese"
              type="month"
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            />
          )}
          
          <ToggleButtonGroup
            color="primary"
            value={severity}
            exclusive
            onChange={(_, value) => value && setSeverity(value)}
            size="small"
          >
            {(['all', 'severe', 'moderate', 'minor'] as SeverityFilter[]).map((key) => (
              <ToggleButton key={key} value={key}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {severityIcons[key]}
                  <span>{severityLabels[key]}</span>
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Cerca per nome, CF o dipartimento"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: { xs: '100%', md: 320 }, flex: 1 }}
          />
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} md={3}>
              <Card sx={{ borderLeft: '4px solid #0d47a1' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Totale anomalie {periodLabels[period].toLowerCase()}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                    {anomalies?.total || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ borderLeft: '4px solid #f44336' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Critiche
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                    {anomalies?.severe || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Medie
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                    {anomalies?.moderate || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Lievi
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                    {anomalies?.minor || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
                    <Typography variant="h6">Maggiore deficit di ore</Typography>
                  </Box>
                  {worstUnderTarget ? (
                    <Box>
                      <Typography variant="subtitle1">
                        {worstUnderTarget.nome} {worstUnderTarget.cognome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Dipartimento: {worstUnderTarget.dipartimento || 'N/D'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Scostamento: <strong>{formatDifference(worstUnderTarget.differenza)}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Completamento: {worstUnderTarget.percentuale_completamento.toFixed(1)}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">Nessuna anomalia in difetto.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <BoltIcon sx={{ color: '#ff9800', mr: 1 }} />
                    <Typography variant="h6">Maggiore extra rispetto al previsto</Typography>
                  </Box>
                  {mostOverTarget ? (
                    <Box>
                      <Typography variant="subtitle1">
                        {mostOverTarget.nome} {mostOverTarget.cognome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Dipartimento: {mostOverTarget.dipartimento || 'N/D'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Ore oltre il target: <strong>{formatDifference(mostOverTarget.differenza)}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Completamento: {mostOverTarget.percentuale_completamento.toFixed(1)}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">Nessuna anomalia di straordinario.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" px={3} py={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QueryStatsIcon color="primary" />
                Elenco anomalie {periodLabels[period]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredEmployees.length} risultati filtrati
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dipendente</TableCell>
                    <TableCell>Dipartimento</TableCell>
                    <TableCell align="center">Periodo</TableCell>
                    <TableCell align="right">Ore previste</TableCell>
                    <TableCell align="right">Ore registrate</TableCell>
                    <TableCell align="right">Scostamento</TableCell>
                    <TableCell align="right">% completamento</TableCell>
                    <TableCell align="center">Gravità</TableCell>
                    <TableCell align="center">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          Nessuna anomalia per i filtri selezionati.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((employee) => {
                      // Formatta il periodo per la visualizzazione
                      const formatPeriod = () => {
                        if (employee.periodo === 'Giornaliero' && employee.anno && employee.mese && employee.giorno) {
                          return `${employee.giorno}/${employee.mese}/${employee.anno}`;
                        } else if (employee.periodo === 'Settimanale' && employee.anno && employee.mese) {
                          return `${employee.mese}/${employee.anno}`;
                        } else if (employee.periodo === 'Mensile' && employee.anno && employee.mese) {
                          return `${employee.mese}/${employee.anno}`;
                        }
                        return employee.periodo;
                      };

                      const handleRowClick = () => {
                        if (employee.user_id && employee.anno && employee.mese) {
                          navigate(`/presence-validation?user_id=${employee.user_id}&year=${employee.anno}&month=${employee.mese}`);
                        }
                      };

                      return (
                      <TableRow
                        key={`${employee.employee_id}-${employee.periodo}`}
                        hover
                        onClick={handleRowClick}
                        sx={{
                          cursor: employee.user_id && employee.anno && employee.mese ? 'pointer' : 'default',
                          backgroundColor:
                            employee.gravita === 'severe'
                              ? 'rgba(244, 67, 54, 0.08)'
                              : employee.gravita === 'moderate'
                              ? 'rgba(255, 152, 0, 0.08)'
                              : 'inherit',
                          '&:hover': {
                            backgroundColor:
                              employee.gravita === 'severe'
                                ? 'rgba(244, 67, 54, 0.15)'
                                : employee.gravita === 'moderate'
                                ? 'rgba(255, 152, 0, 0.15)'
                                : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={600}>
                            {employee.nome} {employee.cognome}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            CF: {employee.codice_fiscale || 'N/D'}
                          </Typography>
                        </TableCell>
                        <TableCell>{employee.dipartimento || 'N/D'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatPeriod()}
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={<CalendarMonthIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (employee.user_id && employee.anno && employee.mese) {
                                navigate(`/presence-validation?user_id=${employee.user_id}&year=${employee.anno}&month=${employee.mese}`);
                              }
                            }}
                            sx={{ cursor: employee.user_id && employee.anno && employee.mese ? 'pointer' : 'default' }}
                          />
                        </TableCell>
                        <TableCell align="right">{formatHours(employee.ore_previste)}</TableCell>
                        <TableCell align="right">{formatHours(employee.ore_effettive)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={formatDifference(employee.differenza)}
                            color={
                              employee.differenza < 0
                                ? 'error'
                                : employee.differenza > 0
                                ? 'info'
                                : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {employee.percentuale_completamento.toFixed(1)}%
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={severityLabels[employee.gravita]}
                            color={severityColors[employee.gravita] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {employee.user_id && employee.anno && employee.mese && (
                              <>
                                <Tooltip title="Risolvi anomalie">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setResolverEmployee({
                                        name: `${employee.nome} ${employee.cognome}`,
                                        userId: employee.user_id!,
                                        year: employee.anno!,
                                        month: employee.mese!,
                                      });
                                      setResolverOpen(true);
                                    }}
                                  >
                                    <BuildIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Apri cartellino">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/presence-validation?user_id=${employee.user_id}&year=${employee.anno}&month=${employee.mese}`);
                                    }}
                                  >
                                    <CalendarMonthIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Apri in Gestione dipendenti">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/employees', {
                                    state: {
                                      focusEmployeeId: employee.employee_id,
                                    },
                                  });
                                }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <Paper variant="outlined">
        <Box px={3} py={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <HourglassIcon color="primary" />
            <Typography variant="h6">Checkout dimenticati</Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadPendingCheckouts}
              disabled={checkoutLoading}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<TaskAltIcon />}
              color="secondary"
              onClick={handleAutoCheckout}
              disabled={checkoutLoading || pendingCheckouts.length === 0}
            >
              Auto checkout (12h)
            </Button>
          </Box>
        </Box>

        <Divider />

        {checkoutLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : pendingCheckouts.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              Nessun checkout dimenticato al momento.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dipendente</TableCell>
                  <TableCell>Check-in</TableCell>
                  <TableCell align="right">Ore aperte</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="center">Azione</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingCheckouts.map((presence) => {
                  const openHours = calcHoursSince(presence.check_in_time);
                  return (
                    <TableRow key={presence.presence_id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>
                          {presence.nome} {presence.cognome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {presence.matricola || 'Matricola N/D'} •{' '}
                          {presence.dipartimento || 'Dipartimento N/D'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {new Date(presence.check_in_time).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${openHours.toFixed(1)} h`}
                          color={openHours >= 12 ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{presence.notes || '-'}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleForceCheckout(presence.presence_id)}
                        >
                          Forza checkout
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Dialog Risoluzione Rapida Anomalie */}
      {resolverEmployee && (
        <QuickAnomalyResolver
          open={resolverOpen}
          onClose={() => {
            setResolverOpen(false);
            setResolverEmployee(null);
          }}
          employeeName={resolverEmployee.name}
          userId={resolverEmployee.userId}
          year={resolverEmployee.year}
          month={resolverEmployee.month}
          onResolved={() => {
            // Ricarica le anomalie dopo la risoluzione
            loadAnomalies(period);
          }}
        />
      )}
    </Container>
  );
};

export default AnomaliesManager;

